import { useEffect, useState } from 'react';

import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

export type UserDetails = {
  email: string;
  supervisoryOrgId: string;
  isAdmin: boolean;
};

// Individual accounts granted KaaP admin access regardless of their org.
const ADMIN_USERS = [
  'armando.collazo@tomtom.com',
  'akhil.krishnakumar@tomtom.com',
  'mustafaerdinc.zorba@tomtom.com',
  'uladzimir.babrou@tomtom.com',
  'semih.ural@tomtom.com',
];

const EMPTY: UserDetails = {
  email: '',
  supervisoryOrgId: '',
  isAdmin: false,
};

/**
 * Resolves the signed-in user from the catalog and decides whether they are a
 * KaaP admin — either by being on the hardcoded allowlist or by belonging to a
 * supervisory org listed in `kaap.adminGroups`.
 */
export const useUser = (): UserDetails => {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const configApi = useApi(configApiRef);

  const [details, setDetails] = useState<UserDetails>(EMPTY);

  useEffect(() => {
    let cancelled = false;
    const adminGroups =
      configApi.getOptionalStringArray('kaap.adminGroups') ?? [];

    (async () => {
      const { userEntityRef } = await identityApi.getBackstageIdentity();
      const userEntity = await catalogApi.getEntityByRef(userEntityRef);
      const annotations = userEntity?.metadata.annotations ?? {};

      const email = annotations['microsoft.com/email'] ?? '';
      const supervisoryOrgId =
        annotations['graph.microsoft.com/supervisory-org-id'] ?? '';

      const isAdmin =
        ADMIN_USERS.includes(email.toLowerCase()) ||
        adminGroups.includes(supervisoryOrgId.toLowerCase());

      if (!cancelled) {
        setDetails({ email, supervisoryOrgId, isAdmin });
      }
    })().catch(() => {
      if (!cancelled) {
        setDetails(EMPTY);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [identityApi, catalogApi, configApi]);

  return details;
};
